import React, { useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Waypoint } from 'react-waypoint';
import ddt from '@wix/da-ddt';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import Button from '../../../Button';

import s from './ThreadPagination.scss';

const logger = ddt.createLogger('comments');

export interface Props {
  streamId: string;
  direction: 'next' | 'prev';
  isInfinite?: boolean;
  hasMore: boolean;
  isFetching: boolean;
  className?: string;
  biData?: BiEvent;
  onLoadMore: () => void;
}

const ThreadPagination: React.FC<React.PropsWithChildren<Props>> = ({
  hasMore,
  isInfinite,
  isFetching,
  onLoadMore,
  className,
  biData,
  direction,
}) => {
  // todo: remove if/when closing T62564
  logger.log('ThreadPagination', {
    hasMore,
    isInfinite,
    isFetching,
    direction,
  });
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();
  const onInfiniteScroll = useCallback(() => {
    if (!biData) {
      return;
    }
    logBiEvent(biData);
    onLoadMore();
  }, [biData, logBiEvent, onLoadMore]);

  if (!hasMore) {
    return null;
  }

  if (isInfinite) {
    return <Waypoint topOffset={100} onEnter={onInfiniteScroll} />;
  }

  if (direction !== 'next') {
    return (
      <div className={s['button-wrap']}>
        <GhostButton
          size={'small'}
          variant={'main'}
          onClick={onLoadMore}
          biData={biData}
          disabled={isFetching}
        >
          {t('commentthread.control.loadprev')}
        </GhostButton>
      </div>
    );
  }

  return (
    <Button
      view="b3"
      onClick={onLoadMore}
      biData={biData}
      disabled={isFetching}
      className={classnames(s['button'], 'ds-card', className)}
    >
      {direction === 'next'
        ? t('commentthread.control.loadmore')
        : t('commentthread.control.loadprev')}
    </Button>
  );
};
ThreadPagination.displayName = 'ThreadPagination';

export default ThreadPagination;
